import { CaretRightOutlined } from "@ant-design/icons";
import { useLazyQuery, useQuery } from "@apollo/react-hooks";
import get from "lodash/get";

import {
  Button,
  Card,
  Collapsible,
  DataTable,
  Scrollable,
  Spinner,
  Stack,
} from "@shopify/polaris";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { REPORT_SALE_SELLERS } from "../../../graphql/queries";
import {
  arrInvalid,
  formatter,
  generateCsvFileName,
  getUnique,
  objectInvalid,
  reducerFn,
} from "../../../helper";
import { EmptyStatePolaris } from "../../shared/EmptyStatePolaris";
import { useReportsContext } from "../context";
import { ComparedPercent } from "./ComparedPercent";
import { useCollapseContext } from "./LayoutSection";
import ShowCard, { conditionByFilter } from "./ShowCard";
import { ImportMinor } from "@shopify/polaris-icons";

const collapseKey = "sellers";
export default function Sellers({ saleChanel }) {
  // Context
  const {
    range,
    filter: filterCtx,
    isCompare,
    rangeToCompare,
    loading: loadingRoot,
    teamID,
  } = useReportsContext();

  const { collapseActive = {}, setCollapseActive = () => {} } =
    useCollapseContext() || {};
  const open = !!collapseActive[collapseKey];

  // State
  const [filter, setFilter] = useState({
    limit: 20,
    offset: 0,
    search: null,
    saleChanel: saleChanel,
    filterByTime: range,
  });
  const [state, setState] = useReducer(reducerFn, {
    rows: [],
    total: 0,
    totalSale: 0,
    totalCost: 0,
    totalRevenues: 0,
    totalKpis: 0,

    loadMore: false,
    cantLoad: false,
    loadOwnerStore: false,
  });
  const [stateCompare, setStateCompare] = useState({
    totalSale: 0,
    totalCost: 0,
    totalRevenues: 0,
  });
  const timeoutRef = useRef(null);

  // Queries
  const { data, loading, fetchMore } = useQuery(REPORT_SALE_SELLERS, {
    variables: {
      filter,
    },
  });

  useQuery(REPORT_SALE_SELLERS, {
    variables: {
      filter: {
        ...filter,
        ...(filter.filterByTime ? { limit: 1000, offset: 0 } : {}),
      },
    },
    onCompleted: (res) => {
      const total = getTotal(res);
      setState((p) => ({ ...p, ...total }));
    },
  });

  const [lazyReport, { data: dataC, loading: loadingC, error: errorC }] =
    useLazyQuery(REPORT_SALE_SELLERS);

  // Get data
  useEffect(() => {
    if (filterCtx != null) {
      let { viewWholeTeamReport, fulfillmentID } = filterCtx;
      setFilter((prev) => ({ ...prev, viewWholeTeamReport, fulfillmentID }));
    }
  }, [filterCtx]);

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      filterByTime: range,
      teamId: teamID ?? undefined,
    }));
    setState({ cantLoad: false });
  }, [range, teamID]);

  const rowsRef = useRef(new Set());

  const tableRef = useRef(null);
  useEffect(() => {
    const nodes = data?.reportSaleBySellers?.nodes;
    const total = data?.reportSaleBySellers?.total;
    let totalKpis = 0;

    const newItems =
      nodes?.length > 0
        ? nodes.map((node, index) => {
            const totalSale = node?.totalSale;
            const totalBaseCost = node?.totalBaseCost || 0;
            const totalRevenues = node?.totalRevenues || 0;
            const user = node?.user;
            const { firstName, lastName } = user || {};

            const fullName = [firstName, lastName].filter(Boolean).join(" ");
            const userId = node?.userId;

            let { total, totalKPIOnline, totalKPIOffline, totalKPIBot } =
              node?.totalKpis || {};
            totalKpis += total || 0;

            total = Number(total || 0).toFixed(2);
            totalKPIOnline = Number(totalKPIOnline || 0).toFixed(2);
            totalKPIOffline = Number(totalKPIOffline || 0).toFixed(2);
            totalKPIBot = Number(totalKPIBot || 0).toFixed(2);

            const newRows = [
              <div className="col-index">{index + 1}</div>,
              <div className="fullname">
                <span>{fullName || userId}</span>
              </div>,
              <div className="total-sale">
                <span>{totalSale}</span>
              </div>,
              <div className="total-cost">
                <span>{formatter.format(totalBaseCost)}</span>
              </div>,
              <div className="total-revenue">
                <span>{formatter.format(totalRevenues)}</span>
              </div>,
              <div className="total-kpi">
                <span>{total}</span>
              </div>,
              <div className="total-kpi-offline">
                <span>{totalKPIOffline}</span>
              </div>,
              <div className="total-kpi-online">
                <span>{totalKPIOnline}</span>
              </div>,
              <div className="total-kpi-online">
                <span>{totalKPIBot}</span>
              </div>,
            ];
            return newRows;
          })
        : [];

    setState((prev) => ({
      ...prev,
      rows: newItems,
      total,
      totalKpis,
    }));
  }, [data]);

  useEffect(() => {
    if (isCompare) {
      let filterByTime = null;
      if (rangeToCompare?.from != null) {
        filterByTime = {
          ...filter.filterByTime,
          ...rangeToCompare,
        };
      }
      lazyReport({
        variables: {
          filter: {
            ...filter,
            filterByTime,
          },
        },
      });
    }
  }, [rangeToCompare, filter, isCompare]);

  useEffect(() => {
    if (isCompare) {
      const totalSale = dataC?.reportSaleBySellers?.totalSale;
      const totalBaseCost = dataC?.reportSaleBySellers?.totalBaseCost;
      const totalRevenues = dataC?.reportSaleBySellers?.totalRevenues;
      setStateCompare((prev) => ({
        ...prev,
        totalSale,
        totalCost: totalBaseCost,
        totalRevenues,
      }));
    } else {
      setStateCompare({ totalSale: 0 });
    }
  }, [dataC, isCompare]);

  // Handles
  const handleScroll = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef);
    }
    if (!state.cantLoad) {
      setState({ loadMore: true });
      timeoutRef.current = setTimeout(() => {
        fetchMore({
          variables: {
            filter: {
              ...filter,
              offset: data.reportSaleBySellers.nodes.length,
            },
          },
          updateQuery: (prev, { fetchMoreResult, variables }) => {
            if (!fetchMoreResult) return prev;
            const reportSaleBySellers = fetchMoreResult.reportSaleBySellers;
            const nodes = reportSaleBySellers.nodes || [];

            const filter = variables.filter;
            const limit = filter.limit;
            setState({ loadMore: false });
            if (nodes.length < limit) {
              setState({ cantLoad: true });
            }

            const newNodes = [...prev.reportSaleBySellers.nodes, ...nodes];

            const result = getUnique(newNodes, "userId");

            const totalCost = (result || []).reduce((acc, item) => {
              if (item?.totalBaseCost > 0) acc += item.totalBaseCost;
              return acc;
            }, 0);

            const totalRevenues = (result || []).reduce((acc, item) => {
              if (item?.totalRevenues > 0) acc += item.totalRevenues;
              return acc;
            }, 0);

            setState((p) => ({
              ...p,
              totalCost: totalCost.toFixed(2),
              totalRevenues: totalRevenues.toFixed(2),
            }));

            return {
              ...prev,
              reportSaleBySellers: {
                ...prev.reportSaleBySellers,
                nodes: result,
              },
            };
          },
        });
      }, 500);
    }
  }, [data, filter, state.cantLoad, fetchMore]);

  const con = conditionByFilter(filterCtx, ["userID"]);

  const btnRef = useRef(null);
  const spinnerMarkup = (
    <Stack distribution="center">
      <Spinner size="small" />
    </Stack>
  );

  const handleTriggerBtn = useCallback(() => {
    btnRef.current && btnRef.current.click();
  }, []);

  const handleToggle = useCallback(() => {
    setCollapseActive((p) => ({ ...p, [collapseKey]: !p[collapseKey] }));
  }, [collapseKey, setCollapseActive]);

  const subTitleMarkup = useMemo(
    () => (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span>
          <span>{state.totalSale} Line items</span>
          <ComparedPercent
            originalVal={state.totalSale}
            newVal={stateCompare.totalSale}
          />
        </span>

        <span>
          <span>Total cost: {formatter.format(state.totalCost)}</span>
          <ComparedPercent
            originalVal={state.totalCost}
            newVal={stateCompare.totalCost}
          />
        </span>
        <span>
          <span>Total revenue: {formatter.format(state.totalRevenues)}</span>
          <ComparedPercent
            originalVal={state.totalRevenues}
            newVal={stateCompare.totalRevenues}
          />
        </span>

        <span>
          <span>Total KPI: {Number(state.totalKpis || 0).toFixed(2)}</span>
        </span>
      </div>
    ),
    [
      state.totalSale,
      stateCompare.totalSale,
      state.totalCost,
      state.totalRevenues,
      stateCompare.totalRevenues,
      stateCompare.totalCost,
      stateCompare.totalKpis,
    ],
  );

  const headerMarkup = useMemo(
    () => (
      <Stack spacing="loose">
        <Stack.Item fill>
          <div>
            <HeadingWrap>
              <span className="btn-wrap">
                <CaretRightOutlined
                  rotate={open ? 90 : 0}
                  style={{ color: "#000" }}
                  ref={btnRef}
                  onClick={handleToggle}
                />
              </span>
              <h2 className="Polaris-Heading" onClick={handleTriggerBtn}>
                Sellers
              </h2>
            </HeadingWrap>
          </div>
        </Stack.Item>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "start",
            columnGap: ".5rem",
          }}
        >
          <Button
            plain
            icon={ImportMinor}
            onClick={() => donwloadSaleByStore(data)}
          />
          {subTitleMarkup}
        </div>
      </Stack>
    ),
    [subTitleMarkup, open],
  );

  return (
    <ShowCard show={con}>
      <Card>
        <Container>
          <Card.Header title={headerMarkup} />
          <Card.Section>
            <div>
              <Collapsible
                open={open}
                id={collapseKey}
                transition={{
                  duration: "500ms",
                  timingFunction: "ease-in-out",
                }}
                expandOnPrint
              >
                {loading || loadingC || loadingRoot ? (
                  spinnerMarkup
                ) : state.rows?.length > 0 ? (
                  <Scrollable
                    shadow
                    id={`scrollable-sellers`}
                    style={{
                      maxHeight: "43rem",
                      paddingRight: "1.6rem",
                      marginRight: "-1.6rem",
                    }}
                    // onScrolledToBottom={handleScroll}
                  >
                    <Stack vertical spacing="loose" wrap={false}>
                      <div ref={tableRef}>
                        <DataTable
                          rows={state.rows}
                          columnContentTypes={[
                            "text",
                            "text",
                            "text",
                            "text",
                            "text",
                            "text",
                            "text",
                            "text",
                            "text",
                          ]}
                          headings={[
                            "#",
                            "Fullname",
                            "Sales",
                            "Total Cost",
                            "Total Revenue",
                            "Total KPI",
                            "Total KPI Offline",
                            "Total KPI Online",
                            "Total KPI Bot",
                          ]}
                          hideScrollIndicator
                        />
                      </div>
                      {state.loadMore && spinnerMarkup}
                    </Stack>
                  </Scrollable>
                ) : (
                  <EmptyStatePolaris noData slim />
                )}
              </Collapsible>
            </div>
          </Card.Section>
        </Container>
      </Card>
    </ShowCard>
  );
}

function getTotal(res) {
  const data = get(res, "reportSaleBySellers");
  const totalSale = get(data, "totalSale");
  const nodes = get(data, "nodes") || [];
  let totalCost = 0;
  let totalRev = 0;

  for (let item of nodes) {
    const totalBaseCost = item?.totalBaseCost || 0;
    const totalRevenues = item?.totalRevenues || 0;
    totalCost += totalBaseCost;
    totalRev += totalRevenues;
  }

  return {
    totalSale,
    totalCost: totalCost.toFixed(2),
    totalRevenues: totalRev.toFixed(2),
  };
}

function donwloadSaleByStore(data) {
  const nodes = get(data, "reportSaleBySellers.nodes") || [];

  if (arrInvalid(nodes)) return;

  let csvContent =
    "Seller,Store,Sales,Total Cost,Total Revenue,Total KPI,Total KPI Offline,Total KPI Online\n";

  for (let node of nodes) {
    if (objectInvalid(node)) continue;

    let {
      totalSale,
      totalBaseCost,
      totalRevenues,
      user,
      totalKpis,
      userId,
      stores,
    } = node;
    const { firstName, lastName } = user || {};
    let { total, totalKPIOnline, totalKPIOffline, totalKPIBot } = totalKpis || {};

    total = Number(total || 0).toFixed(2);
    totalKPIOnline = Number(totalKPIOnline || 0).toFixed(2);
    totalKPIOffline = Number(totalKPIOffline || 0).toFixed(2);
    totalKPIBot = Number(totalKPIBot || 0).toFixed(2);

    const fName = [firstName, lastName].filter(Boolean).join(" ") || userId;
    totalBaseCost = formatter.format(totalBaseCost);
    totalRevenues = formatter.format(totalRevenues);
    csvContent += `"${fName}","",${totalSale},"${totalBaseCost}","${totalRevenues}",${total},${totalKPIOffline},${totalKPIOnline},${totalKPIBot}\n`;

    if (stores?.length > 0) {
      for (let storeItem of stores) {
        let { totalSale, totalBaseCost, totalRevenues, totalKpis, store } =
          storeItem;

        totalBaseCost = formatter.format(totalBaseCost);
        totalRevenues = formatter.format(totalRevenues);
        let { title, id } = store || {};

        title = title || id;
        let { total, totalKPIOnline, totalKPIOffline, totalKPIBot } = totalKpis || {};
        total = Number(total || 0).toFixed(2);
        totalKPIOnline = Number(totalKPIOnline || 0).toFixed(2);
        totalKPIOffline = Number(totalKPIOffline || 0).toFixed(2);
        totalKPIBot = Number(totalKPIBot || 0).toFixed(2);
        csvContent += `"","${title}",${totalSale},"${totalBaseCost}","${totalRevenues}",${total},${totalKPIOffline},${totalKPIOnline},${totalKPIBot}\n`;
      }
    }
  }

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = generateCsvFileName(`export_sale_by_seller_data_`);
  a.click();
  URL.revokeObjectURL(url);
}

const Container = styled.div`
  .item {
    white-space: normal;
    word-break: break-word;
  }

  .col-index {
    width: 20px;
    display: inline-block;
  }

  .fullname {
    white-space: break-spaces;
    width: 300px;
  }

  .total-sale,
  .total-cost,
  .total-kpi,
  .total-kpi-offline,
  .total-kpi-online,
  .total-kpi-bot,
  .total-revenue {
    width: 100px;
    display: inline-block;
  }

  .Polaris-DataTable__Cell {
    &.slot {
      padding: 0;
      display: table-cell !important;

      .Polaris-DataTable__Cell {
        background-color: #f9fafb;
        padding-top: 5px;
        padding-bottom: 5px;
      }

      .store-owner {
        display: flex;
        overflow: hidden;
        flex-wrap: wrap;
        white-space: break-spaces;
        padding-left: 16px;
        width: 300px;
      }
    }

    &:has(.owner-store-slot) {
      display: none;
    }
  }
`;

const HeadingWrap = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-left: -1.4rem;

  .Polaris-Heading {
    cursor: pointer;
  }

  .btn-wrap {
    margin-top: 0.2rem;
    margin-right: 0.5rem;
  }
`;
